import type { ResolvedConfiguration } from './types';

/**
 * Reading `presentation` out of a resolved configuration. Ported from portal-ui-lib's
 * `configuration-presentation.util.ts`.
 *
 * This is how a screen gets its user-visible text: the backend resolves the feature schema's
 * `translations` into `presentation.texts` in the user's language, so a screen reads strings
 * from here and never hardcodes them (CLAUDE.md, Agent Mistakes Log #1).
 */

/** Flat or nested text maps under `presentation.texts` and sub-features. */
export type PresentationTexts = Record<string, unknown>;

type Configuration = ResolvedConfiguration | null | undefined;

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * One string from a text map. Text maps mix nested groups and plain strings, so anything that
 * is not a string reads as `fallback`.
 */
export function getPresentationText(
  texts: PresentationTexts | null | undefined,
  key: string,
  fallback = '',
): string {
  const value = texts?.[key];
  return typeof value === 'string' ? value : fallback;
}

/** `presentation.texts`, with `presentation.title` merged in as `title`. */
export function extractTranslations(
  configuration: Configuration,
  defaultTitle = '',
): PresentationTexts {
  const presentation = configuration?.presentation ?? {};
  const title = presentation['title'];
  return {
    title: typeof title === 'string' ? title : defaultTitle,
    ...(asRecord(presentation['texts']) ?? {}),
  };
}

/**
 * `presentation.texts.<subFeatureKey>` — where tabbed and administration features keep each
 * sub-screen's texts. `organizationManagement` also answers from `orgNodeManagement`, the name
 * older payloads still use.
 */
export function extractSubFeatureTexts(
  configuration: Configuration,
  subFeatureKey: string,
): PresentationTexts {
  const texts = asRecord(configuration?.presentation?.['texts']);
  if (!texts) {
    return {};
  }
  const own = asRecord(texts[subFeatureKey]);
  if (own) {
    return own;
  }
  if (subFeatureKey === 'organizationManagement') {
    return asRecord(texts['orgNodeManagement']) ?? {};
  }
  return {};
}

/** `presentation.<subFeatureKey>`, or the first of `aliases` that is present. */
export function extractSubFeaturePresentation(
  configuration: Configuration,
  subFeatureKey: string,
  aliases: readonly string[] = [],
): PresentationTexts | null {
  const presentation = asRecord(configuration?.presentation);
  if (!presentation) {
    return null;
  }
  for (const key of [subFeatureKey, ...aliases]) {
    const value = asRecord(presentation[key]);
    if (value) {
      return value;
    }
  }
  return null;
}

/** A presentation flag sent either as a boolean or as `{ value: boolean }`. */
export function extractPresentationBooleanFlag(value: unknown): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  const record = asRecord(value);
  if (record && 'value' in record) {
    return record['value'] !== false;
  }
  return false;
}

/** `presentation.table` (header rows, data columns, column definitions), or null. */
export function extractTablePresentation(configuration: Configuration): PresentationTexts | null {
  return asRecord(configuration?.presentation?.['table']);
}

/** One nested group from a text map (e.g. `table`, `tyreTypeToggle`), or empty. */
export function extractTextGroup(
  translations: PresentationTexts | null | undefined,
  groupKey: string,
): Record<string, string> {
  const group = asRecord(translations?.[groupKey]);
  return (group ?? {}) as Record<string, string>;
}

/**
 * The strings a screen reads, picked out of a text map. A key the map lacks reads as the key
 * itself — visible enough to be caught in review, and each feature's tests check its key list
 * against the backend schema's translations so it does not reach a user.
 */
export function pickTexts<K extends string>(
  texts: PresentationTexts | null | undefined,
  keys: readonly K[],
): Record<K, string> {
  const picked = {} as Record<K, string>;
  for (const key of keys) {
    picked[key] = getPresentationText(texts, key, key);
  }
  return picked;
}

/** Several text groups at once: `{ group: keys }` → `{ group: { key: text } }`. */
export type TextGroups<G extends Record<string, readonly string[]>> = {
  [Group in keyof G]: Record<G[Group][number], string>;
};

/**
 * `presentation.texts.<group>` for each group, narrowed to its keys (see {@link pickTexts} for
 * missing keys).
 */
export function pickTextGroups<G extends Record<string, readonly string[]>>(
  configuration: Configuration,
  groups: G,
): TextGroups<G> {
  const picked = {} as Record<string, Record<string, string>>;
  for (const [group, keys] of Object.entries(groups)) {
    picked[group] = pickTexts(extractSubFeatureTexts(configuration, group), keys);
  }
  return picked as TextGroups<G>;
}

/** A list's page sizes: the options the pager offers and the one it starts on. */
export interface PagingPreset {
  options: readonly number[];
  defaultSize: number;
}

/**
 * `presentation.pageSizeOptions` / `presentation.defaultPageSize` — a feature's (or a tenant's
 * override of its) page sizes — over `fallback`, the screen's own. Anything malformed reads as
 * the fallback, and a default that is not among the options falls back to the first option.
 */
export function extractPaging(configuration: Configuration, fallback: PagingPreset): PagingPreset {
  const presentation = configuration?.presentation ?? {};
  const raw = presentation['pageSizeOptions'];
  const configured = Array.isArray(raw)
    ? raw.filter((value): value is number => Number.isInteger(value) && value > 0)
    : [];
  const options = configured.length > 0 ? configured : fallback.options;
  const wanted = presentation['defaultPageSize'];
  const defaultSize = [wanted, fallback.defaultSize].find(
    (size): size is number => typeof size === 'number' && options.includes(size),
  );
  return { options, defaultSize: defaultSize ?? options[0]! };
}
